import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import {
    Box, Typography, Button, Paper, Stack,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, MenuItem, FormControl, InputLabel, Select, LinearProgress
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon, Save as SaveIcon } from '@mui/icons-material';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { Alert, Snackbar } from '@mui/material';
import AppHeader from '../../components/AppHeader';

const AccountingMap = ({ setIsLoggedIn }) => {
    const tenant = localStorage.getItem('tenant') || '';
    const [maps, setMaps] = useState([]);
    const [elements, setElements] = useState([]);
    const [costCentres, setCostCentres] = useState([]);
    const [loading, setLoading] = useState(false);
    
    const [openDialog, setOpenDialog] = useState(false);
    const [formData, setFormData] = useState({
        element_id: '',
        cost_centre_id: '',
        account_code: '',
        account_name: '',
        debit_credit: 'DEBIT'
    });
    const [statusMsg, setStatusMsg] = useState({ open: false, msg: '', severity: 'success' });

    const gridRef = useRef();

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const h = { "X-Tenant": tenant };
            const [mapsRes, elRes, ccRes] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/accounting-maps`, { withCredentials: true, headers: h }),
                axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/elements`, { withCredentials: true, headers: h }),
                axios.get(`${process.env.REACT_APP_API_URL1}/som/cost-centres`, { withCredentials: true, headers: h })
            ]);

            if (mapsRes.data.success) setMaps(mapsRes.data.data);
            if (elRes.data.success) setElements(elRes.data.data);
            if (ccRes.data.success) setCostCentres(ccRes.data.data.filter(c => c.is_active === 1 || c.is_active === true));
        } catch (error) {
            console.error("Failed to fetch accounting mapping data:", error);
            setStatusMsg({ open: true, msg: 'Failed to fetch data', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [tenant]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleOpen = () => {
        setFormData({
            element_id: '',
            cost_centre_id: '',
            account_code: '',
            account_name: '',
            debit_credit: 'DEBIT'
        });
        setOpenDialog(true);
    };

    const handleSave = async () => {
        if (!formData.element_id || !formData.cost_centre_id || !formData.account_code) {
            setStatusMsg({ open: true, msg: 'Please select Pay Element, Cost Centre, and provide Account Code.', severity: 'warning' });
            return;
        }

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_API_URL1}/sm/payroll/accounting-maps`,
                { mappings: [formData] },
                { withCredentials: true, headers: { "X-Tenant": tenant } }
            );

            if (res.data.success) {
                setStatusMsg({ open: true, msg: 'Mapping saved successfully', severity: 'success' });
                setOpenDialog(false);
                fetchData();
            }
        } catch (error) {
            console.error("Error saving mapping:", error);
            setStatusMsg({ open: true, msg: error.response?.data?.error || error.message, severity: 'error' });
        }
    };

    const columnDefs = useMemo(() => [
        { headerName: 'Cost Centre', field: 'cost_centre_name', filter: true, sortable: true, width: 250, valueGetter: p => `${p.data.cost_centre_name} (${p.data.cost_centre_code})` },
        { headerName: 'Pay Element', field: 'element_name', filter: true, sortable: true, width: 250 },
        { headerName: 'Element Type', field: 'type', filter: true, sortable: true, width: 150 },
        { headerName: 'Account Code', field: 'account_code', filter: true, sortable: true, width: 150 },
        { headerName: 'Account Name', field: 'account_name', filter: true, sortable: true, width: 250 },
        { headerName: 'Debit / Credit', field: 'debit_credit', filter: true, sortable: true, width: 150 }
    ], []);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f8fafc' }}>
            <AppHeader title="Accounting Mapping Setup" setIsLoggedIn={setIsLoggedIn} />

            <Box sx={{ p: 4, flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 1400, mx: 'auto', width: '100%' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', mb: 0.5 }}>
                            Accounting Mapping Setup
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                            Map payroll elements to GL accounts by Cost Centre for Journal Export.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={2}>
                        <Button 
                            variant="outlined" 
                            startIcon={<RefreshIcon />}
                            onClick={fetchData}
                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, borderColor: '#cbd5e1', color: '#475569', bgcolor: '#fff', '&:hover': { bgcolor: '#f8fafc' } }}
                        >
                            Refresh
                        </Button>
                        <Button 
                            variant="contained" 
                            startIcon={<AddIcon />}
                            onClick={handleOpen}
                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, bgcolor: 'primary.main', boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}33`, '&:hover': { bgcolor: 'primary.dark' } }}
                        >
                            Add Mapping
                        </Button>
                    </Stack>
                </Stack>

                <Paper sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 20px 0 rgba(0,0,0,0.03)', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ flexGrow: 1, p: 1, bgcolor: '#fff' }}>
                        {loading && <LinearProgress sx={{ borderRadius: 1, mb: 1, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' } }} />}
                        <div className="ag-theme-quartz" style={{ height: '100%', width: '100%' }}>
                            <AgGridReact
                                ref={gridRef}
                                rowData={maps}
                                columnDefs={columnDefs}
                                defaultColDef={{ resizable: true, sortable: true, filter: true }}
                                animateRows={true}
                                rowSelection="single"
                                overlayLoadingTemplate={'<span class="ag-overlay-loading-center">Loading Data...</span>'}
                                overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">No Mappings Found</span>'}
                            />
                        </div>
                    </Box>
                </Paper>

                <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ fontWeight: 800, color: '#0f172a' }}>Add / Edit Mapping</DialogTitle>
                    <DialogContent dividers sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Cost Centre</InputLabel>
                            <Select
                                value={formData.cost_centre_id}
                                label="Cost Centre"
                                onChange={(e) => setFormData({ ...formData, cost_centre_id: e.target.value })}
                            >
                                {costCentres.map(c => (
                                    <MenuItem key={c.id} value={c.id}>{c.name} ({c.code})</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth size="small">
                            <InputLabel>Pay Element</InputLabel>
                            <Select
                                value={formData.element_id}
                                label="Pay Element"
                                onChange={(e) => setFormData({ ...formData, element_id: e.target.value })}
                            >
                                {elements.map(e => (
                                    <MenuItem key={e.id} value={e.id}>{e.element_name} ({e.element_type})</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label="GL Account Code"
                            size="small"
                            fullWidth
                            value={formData.account_code}
                            onChange={(e) => setFormData({ ...formData, account_code: e.target.value })}
                        />

                        <TextField
                            label="Account Name"
                            size="small"
                            fullWidth
                            value={formData.account_name}
                            onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                        />

                        <FormControl fullWidth size="small">
                            <InputLabel>Debit / Credit</InputLabel>
                            <Select
                                value={formData.debit_credit}
                                label="Debit / Credit"
                                onChange={(e) => setFormData({ ...formData, debit_credit: e.target.value })}
                            >
                                <MenuItem value="DEBIT">Debit</MenuItem>
                                <MenuItem value="CREDIT">Credit</MenuItem>
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions sx={{ p: 2, px: 3, bgcolor: '#f8fafc' }}>
                        <Button onClick={() => setOpenDialog(false)} sx={{ color: '#64748b', fontWeight: 600, textTransform: 'none' }}>Cancel</Button>
                        <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />} sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' }, color: '#fff', fontWeight: 700, borderRadius: 2, textTransform: 'none' }}>
                            Save Mapping
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar open={statusMsg.open} autoHideDuration={6000} onClose={() => setStatusMsg({ ...statusMsg, open: false })}>
                    <Alert onClose={() => setStatusMsg({ ...statusMsg, open: false })} severity={statusMsg.severity} sx={{ width: '100%' }}>
                        {statusMsg.msg}
                    </Alert>
                </Snackbar>
            </Box>
        </Box>
    );
};

export default AccountingMap;
